import { IClassWithGroups } from "ts/interfaces/Transaction"

import { ClassnameProps } from "../AllTransactionsTeacher"

export const typeTransactions = [
  "Bonus",
  "Fine",
  "Salary",
  "Expense",
  "Refund",
  "Interest",
  "Purchase",
]

export interface IClassTreeSelection {
  classIds: number[]
  groupIds: number[]
}

export type ClassCheckState = "checked" | "indeterminate" | "unchecked"

const getGroupIds = (classItem: IClassWithGroups) =>
  (classItem.groups ?? []).map(({ id }) => id)

export const getClassCheckState = (
  selection: IClassTreeSelection,
  classItem: IClassWithGroups,
): ClassCheckState => {
  if (selection.classIds.includes(classItem.id)) return "checked"
  if (getGroupIds(classItem).some((id) => selection.groupIds.includes(id)))
    return "indeterminate"
  return "unchecked"
}

export const toggleClass = (
  selection: IClassTreeSelection,
  classItem: IClassWithGroups,
): IClassTreeSelection => {
  if (selection.classIds.includes(classItem.id)) {
    return {
      ...selection,
      classIds: selection.classIds.filter((id) => id !== classItem.id),
    }
  }

  const groupIds = getGroupIds(classItem)
  return {
    classIds: [...selection.classIds, classItem.id],
    groupIds: selection.groupIds.filter((id) => !groupIds.includes(id)),
  }
}

export const toggleGroup = (
  selection: IClassTreeSelection,
  classItem: IClassWithGroups,
  groupId: number,
): IClassTreeSelection => {
  const groupIds = getGroupIds(classItem)
  const otherGroupIds = selection.groupIds.filter(
    (id) => !groupIds.includes(id),
  )
  const otherClassIds = selection.classIds.filter((id) => id !== classItem.id)

  const tickedGroupIds = selection.classIds.includes(classItem.id)
    ? groupIds
    : groupIds.filter((id) => selection.groupIds.includes(id))
  const nextTickedGroupIds = tickedGroupIds.includes(groupId)
    ? tickedGroupIds.filter((id) => id !== groupId)
    : [...tickedGroupIds, groupId]

  if (nextTickedGroupIds.length === groupIds.length) {
    return {
      classIds: [...otherClassIds, classItem.id],
      groupIds: otherGroupIds,
    }
  }

  return {
    classIds: otherClassIds,
    groupIds: [...otherGroupIds, ...nextTickedGroupIds],
  }
}

export const pruneClassTreeSelection = (
  selection: IClassTreeSelection,
  classes: IClassWithGroups[],
): IClassTreeSelection => {
  const classIds = classes.map(({ id }) => id)
  const groupIds = classes.flatMap(getGroupIds)
  const prunedClassIds = selection.classIds.filter((id) =>
    classIds.includes(id),
  )
  const prunedGroupIds = selection.groupIds.filter((id) =>
    groupIds.includes(id),
  )

  if (
    prunedClassIds.length === selection.classIds.length &&
    prunedGroupIds.length === selection.groupIds.length
  ) {
    return selection
  }

  return { classIds: prunedClassIds, groupIds: prunedGroupIds }
}

export const buildClassFilterChips = (
  selection: IClassTreeSelection,
  classes: IClassWithGroups[],
): ClassnameProps[] =>
  classes.flatMap((classItem): ClassnameProps[] => {
    if (selection.classIds.includes(classItem.id)) {
      return [{ kind: "class", id: classItem.id, name: classItem.name }]
    }

    return (classItem.groups ?? [])
      .filter(({ id }) => selection.groupIds.includes(id))
      .map(({ id, name }) => ({
        kind: "group",
        id,
        name: `${classItem.name} · ${name}`,
      }))
  })

export const removeClassFilterChip = (
  selection: IClassTreeSelection,
  chip: ClassnameProps,
): IClassTreeSelection =>
  chip.kind === "class"
    ? {
        ...selection,
        classIds: selection.classIds.filter((id) => id !== chip.id),
      }
    : {
        ...selection,
        groupIds: selection.groupIds.filter((id) => id !== chip.id),
      }
